import i18n from 'i18next';
import keyMirror from 'keymirror';

import type { OnboardingMessageContent } from '@/components/onboardingMessages/types';

export const onboardingMessagesActionTypes = keyMirror({
	SET_ONBOARDING_MESSAGES_TYPE: null,
});

export const newUserMessages = (data: any): OnboardingMessageContent[] => [
	{ id: 'new-user-1', text: String(i18n.t('onboarding_messages.new_user.message_1', data)) },
	{ id: 'new-user-2', text: String(i18n.t('onboarding_messages.new_user.message_2', data)) },
	{ id: 'new-user-3', text: String(i18n.t('onboarding_messages.new_user.message_3', data)) },
];

export const userStickerMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'user-sticker-1',
		text: String(i18n.t('onboarding_messages.user_sticker.message_1', data)),
	},
	{
		id: 'user-sticker-2',
		text: String(i18n.t('onboarding_messages.user_sticker.message_2', data)),
	},
];

export const userReportMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'user-report-1',
		text: String(i18n.t('onboarding_messages.user_report.message_1', data)),
	},
	{
		id: 'user-report-2',
		text: String(i18n.t('onboarding_messages.user_report.message_2', data)),
	},
	{
		id: 'user-report-3',
		text: String(i18n.t('onboarding_messages.user_report.message_3', data)),
	},
];

export const weekGoalMessages = (data: any): OnboardingMessageContent[] => [
	{ id: 'week-goal-1', text: String(i18n.t('onboarding_messages.week_goal.message_1', data)) },
	{ id: 'week-goal-2', text: String(i18n.t('onboarding_messages.week_goal.message_2', data)) },
];

export const freePlayWorldMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'free-play-world-1',
		text: String(i18n.t('onboarding_messages.free_play_world.message_1', data)),
	},
	{
		id: 'free-play-world-2',
		text: String(i18n.t('onboarding_messages.free_play_world.message_2', data)),
	},
	{
		id: 'free-play-world-3',
		text: String(i18n.t('onboarding_messages.free_play_world.message_3', data)),
	},
];

export const freePlayCategoryMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'free-play-category-1',
		text: String(i18n.t('onboarding_messages.free_play_category.message_1', data)),
	},
	{
		id: 'free-play-category-2',
		text: String(i18n.t('onboarding_messages.free_play_category.message_2', data)),
	},
	{
		id: 'free-play-category-3',
		text: String(i18n.t('onboarding_messages.free_play_category.message_3', data)),
	},
];

export const firstStarEarnedMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'first-star-earned-1',
		text: String(
			i18n.t('onboarding_messages.first_star_earned.message_1', {
				count: data.starsEarned,
			}),
		),
	},
	{
		id: 'first-star-earned-2',
		text: String(
			i18n.t('onboarding_messages.first_star_earned.message_2', { count: data.starsLeft }),
		),
	},
	{
		id: 'first-star-earned-3',
		text: String(i18n.t('onboarding_messages.first_star_earned.message_3', data)),
	},
];

export const firstAchievementEarnedMessages = (data: any): OnboardingMessageContent[] => [
	{
		id: 'first-achievement-earned-1',
		text: String(i18n.t('onboarding_messages.first_achievement_earned.message_1', data)),
	},
	{
		id: 'first-achievement-earned-2',
		text: String(i18n.t('onboarding_messages.first_achievement_earned.message_2', data)),
	},
];

export const goalSettingsIntroMessages = (data: any): OnboardingMessageContent[] => {
	return [
		{
			id: 'goal-settings-intro-1',
			text: String(i18n.t('onboarding_messages.goal_settings_intro.message_1', data)),
		},
		{
			id: 'goal-settings-intro-2',
			text: String(i18n.t('onboarding_messages.goal_settings_intro.message_2', data)),
		},
		{
			id: 'goal-settings-intro-3',
			text: String(i18n.t('onboarding_messages.goal_settings_intro.message_3', data)),
			actions: [
				{
					id: 'goal-settings-intro-parent',
					label: String(
						i18n.t('onboarding_messages.goal_settings_intro.button_parent', data),
					),
					onClick: data?.onGoalSettingsParentClick ?? (() => {}),
					type: 'secondary',
					color: 'white',
				},
				{
					id: 'goal-settings-intro-child',
					label: String(
						i18n.t('onboarding_messages.goal_settings_intro.button_child', data),
					),
					onClick: data?.onGoalSettingsChildClick ?? (() => {}),
					type: 'secondary',
					color: 'white',
				},
			],
		},
	];
};

export const goalSettingsParentMessages = (data: any): OnboardingMessageContent[] => {
	return [
		{
			id: 'goal-settings-parent-1',
			text: String(i18n.t('onboarding_messages.goal_settings_parent.message_1', data)),
			actions: [
				{
					id: 'goal-settings-parent-now',
					label: String(
						i18n.t('onboarding_messages.goal_settings_parent.button_setup', data),
					),
					onClick: data?.onGoalSettingsParentConfirmNow ?? (() => {}),
					type: 'primary',
					iconName: 'icon-arrow-circle-right',
				},
				{
					id: 'goal-settings-parent-later',
					label: String(
						i18n.t('onboarding_messages.goal_settings_parent.button_later', data),
					),
					onClick: data?.onGoalSettingsParentConfirmLater ?? (() => {}),
					type: 'secondary',
					color: 'white',
					iconName: 'icon-clock',
				},
			],
		},
	];
};

export const goalSettingsParentFollowUpMessages = (data: any): OnboardingMessageContent[] => {
	return [
		{
			id: 'goal-settings-parent-follow-up-1',
			text: String(
				i18n.t('onboarding_messages.goal_settings_parent_follow_up.message_1', data),
			),
			actions: [
				{
					id: 'goal-settings-parent-follow-up-setup',
					label: String(
						i18n.t(
							'onboarding_messages.goal_settings_parent_follow_up.button_setup',
							data,
						),
					),
					onClick: data?.onGoalSettingsParentConfirmNow ?? (() => {}),
					type: 'primary',
					iconName: 'icon-arrow-circle-right',
				},
				{
					id: 'goal-settings-parent-follow-up-default',
					label: String(
						i18n.t(
							'onboarding_messages.goal_settings_parent_follow_up.button_default',
							data,
						),
					),
					onClick: data?.onGoalSettingsParentContinueWithDefault ?? (() => {}),
					type: 'secondary',
					color: 'white',
					iconName: 'icon-clock',
					active: true,
				},
			],
		},
		{
			id: 'goal-settings-parent-follow-up-2',
			text: String(
				i18n.t('onboarding_messages.goal_settings_parent_follow_up.message_2', data),
			),
		},
		{
			id: 'goal-settings-parent-follow-up-3',
			text: String(
				i18n.t('onboarding_messages.goal_settings_parent_follow_up.message_3', data),
			),
			actions: [
				{
					id: 'goal-settings-parent-follow-up-close',
					label: String(
						i18n.t(
							'onboarding_messages.goal_settings_parent_follow_up.button_close_chat',
							data,
						),
					),
					onClick: data?.onCloseChat ?? (() => {}),
					type: 'primary',
					color: 'blue',
					iconName: 'icon-close',
				},
			],
		},
	];
};

export const learningPathGameIntroMessages = () => [
	{
		id: 'learning-path-game-intro',
		text: String(i18n.t('onboarding_messages.learning_path_game_intro.message')),
	},
];

export const learningPathGameCompletedMessages = () => [
	{
		id: 'learning-path-game-completed',
		text: String(i18n.t('onboarding_messages.learning_path_game_completed.message')),
	},
];

export const ONBOARDING_MESSAGES_TYPES = {
	NEW_USER: 'newUser',
	USER_STICKER: 'userSticker',
	USER_REPORT: 'userReport',
	WEEK_GOAL: 'weekGoal',
	FREE_PLAY_WORLD: 'freePlayWorld',
	FREE_PLAY_CATEGORY: 'freePlayCategory',
	FIRST_STAR_EARNED: 'firstStarEarned',
	FIRST_ACHIEVEMENT_EARNED: 'firstAchievementEarned',
	GOAL_SETTINGS_INTRO: 'goalSettingsIntro',
	GOAL_SETTINGS_PARENT_PROMPT: 'goalSettingsParentPrompt',
	GOAL_SETTINGS_PARENT_FOLLOW_UP: 'goalSettingsParentFollowUp',
	LEARNING_PATH_GAME_INTRO: 'learningPathGameIntro',
	LEARNING_PATH_GAME_COMPLETED: 'learningPathGameCompleted',
};

export const ANIMATE_HIGHLIGHT = '.animateHighlight';

export const STICKERS_DROPDOWN = '.stickersDropdown';

export const DAILY_REPORT_DROPDOWN = '.dailyReportDropdown';

export const WEEK_GOAL_DROPDOWN = '.weekGoalDropdown';

export const HIGHLIGHTED_BORDER = 'highlighted-border';

export const HEIGHT_OF_CHAT_WITHOUT_MESSAGES = 126;
